import { useState, useRef, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { Plus, Search, Edit2, Trash2, Wrench, Package, CheckCircle, Clock, AlertCircle, X, Upload, Camera, Image as ImageIcon, Calendar, ArrowUpDown, Eye, Download } from "lucide-react";
import axios from "axios";
import ImportExportButtons from "../../components/ImportExportButtons";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Format date as dd/mm/yyyy in Singapore timezone (UTC+8)
const formatDateDMY = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  // Format in Singapore timezone
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'Asia/Singapore'
  });
};

// Format datetime as dd/mm/yyyy HH:mm in Singapore timezone
const formatDateTimeSGT = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Asia/Singapore'
  });
};

const getStatusColor = (status) => {
  switch (status) {
    case 'pending': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
    case 'in_progress': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
    case 'completed': return 'bg-green-500/20 text-green-400 border-green-500/30';
    default: return 'bg-white/10 text-white/60 border-white/20';
  }
};

const getStatusIcon = (status) => {
  switch (status) {
    case 'pending': return <Clock className="w-4 h-4" />;
    case 'in_progress': return <AlertCircle className="w-4 h-4" />;
    case 'completed': return <CheckCircle className="w-4 h-4" />;
    default: return null;
  }
};

export default function RepairsTab({ drivers = [], user = null }) {
  const [repairs, setRepairs] = useState([]);
  const [mechanics, setMechanics] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingRepair, setEditingRepair] = useState(null);
  const [addingItemToRepair, setAddingItemToRepair] = useState(null);
  const [repairTypes, setRepairTypes] = useState([]);
  const [newRepairType, setNewRepairType] = useState("");
  const [showAddType, setShowAddType] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [editTypeName, setEditTypeName] = useState("");
  const [creatingRepair, setCreatingRepair] = useState(false);
  const [newRepair, setNewRepair] = useState({
    repair_type: "",
    mechanic_id: "",
    vehicle_number: "",
    damage_photo: "",
    vehicle_photo: "",
    mechanicSearch: ""
  });
  const [itemToAdd, setItemToAdd] = useState({ item_id: "", quantity: 1, itemSearch: "" });
  const damagePhotoRef = useRef(null);
  const vehiclePhotoRef = useRef(null);
  
  // Photo viewer state
  const [viewingPhotos, setViewingPhotos] = useState(null);
  const [photosLoading, setPhotosLoading] = useState(false);
  const [repairPhotos, setRepairPhotos] = useState({ damage_photo: null, vehicle_photo: null });

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      const timestamp = Date.now();
      const [repairsRes, mechanicsRes, inventoryRes, typesRes] = await Promise.all([
        axios.get(`${API}/repairs?_t=${timestamp}`),
        axios.get(`${API}/mechanics?_t=${timestamp}`),
        axios.get(`${API}/inventory?_t=${timestamp}`),
        axios.get(`${API}/repair-types?_t=${timestamp}`)
      ]);
      setRepairs(repairsRes.data || []);
      setMechanics(mechanicsRes.data || []);
      setInventory(inventoryRes.data || []);
      setRepairTypes(typesRes.data || []);
    } catch (error) {
      console.error("Error fetching repairs data:", error);
      toast.error("Failed to load repairs data");
    } finally {
      setLoading(false);
    }
  };

  const fetchRepairs = async () => {
    try {
      const res = await axios.get(`${API}/repairs?_t=${Date.now()}`);
      setRepairs(res.data || []);
    } catch (error) {
      console.error("Error fetching repairs:", error);
    }
  };

  const fetchInventory = async () => {
    try {
      const res = await axios.get(`${API}/inventory`);
      setInventory(res.data || []);
    } catch (error) {
      console.error("Error fetching inventory:", error);
    }
  };

  const fetchRepairTypes = async () => {
    try {
      const res = await axios.get(`${API}/repair-types`);
      setRepairTypes(res.data || []);
    } catch (error) {
      console.error("Error fetching repair types:", error);
    }
  };

  // Filter mechanics for autocomplete
  const filteredMechanics = mechanics.filter(m =>
    (m.name || m.full_name)?.toLowerCase().includes(newRepair.mechanicSearch.toLowerCase())
  );

  // Sorting function
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Filtered and sorted repairs
  const filteredRepairs = useMemo(() => {
    let filtered = repairs.filter(r => {
      const matchesSearch = (r.title || r.repair_type)?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.mechanic_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.vehicle_number?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === "all" || r.status === statusFilter;
      
      // Date range filter
      let matchesDateRange = true;
      if (startDate || endDate) {
        const repairDate = r.created_at ? r.created_at.split('T')[0] : '';
        if (startDate && repairDate < startDate) matchesDateRange = false;
        if (endDate && repairDate > endDate) matchesDateRange = false;
      }
      
      return matchesSearch && matchesStatus && matchesDateRange;
    });

    // Sort
    filtered.sort((a, b) => {
      let aVal, bVal;
      switch (sortConfig.key) {
        case 'created_at':
          aVal = a.created_at || '';
          bVal = b.created_at || '';
          break;
        case 'repair_type':
          aVal = (a.title || a.repair_type || '').toLowerCase();
          bVal = (b.title || b.repair_type || '').toLowerCase();
          break;
        case 'mechanic_name':
          aVal = (a.mechanic_name || '').toLowerCase();
          bVal = (b.mechanic_name || '').toLowerCase();
          break;
        case 'vehicle_number':
          aVal = (a.vehicle_number || '').toLowerCase();
          bVal = (b.vehicle_number || '').toLowerCase();
          break;
        case 'status':
          aVal = a.status || '';
          bVal = b.status || '';
          break;
        case 'total_parts_cost':
          aVal = a.total_parts_cost || 0;
          bVal = b.total_parts_cost || 0;
          break;
        default:
          aVal = a.created_at || '';
          bVal = b.created_at || '';
      }
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [repairs, searchTerm, statusFilter, startDate, endDate, sortConfig]);

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm("");
    setStatusFilter("all");
    setStartDate("");
    setEndDate("");
  };

  // Filter inventory for item autocomplete
  const filteredItems = inventory.filter(i =>
    i.name?.toLowerCase().includes(itemToAdd.itemSearch.toLowerCase()) && i.quantity > 0
  );

  // Compress image to target size (200KB max)
  const compressImage = (file, maxSizeKB) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new window.Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          // Start with smaller dimensions for better compression
          const maxDimension = 1000;
          if (width > maxDimension || height > maxDimension) {
            if (width > height) {
              height = Math.round((height * maxDimension) / width);
              width = maxDimension;
            } else {
              width = Math.round((width * maxDimension) / height);
              height = maxDimension;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Start with quality 0.7 and reduce until under target size
          let quality = 0.7;
          let dataUrl = canvas.toDataURL('image/jpeg', quality);
          
          const targetBase64Size = maxSizeKB * 1024 * 1.37;
          
          while (dataUrl.length > targetBase64Size && quality > 0.1) {
            quality -= 0.05;
            dataUrl = canvas.toDataURL('image/jpeg', quality);
          }
          
          // If still too large, reduce dimensions further
          if (dataUrl.length > targetBase64Size) {
            const scale = 0.7;
            canvas.width = Math.round(width * scale);
            canvas.height = Math.round(height * scale);
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            dataUrl = canvas.toDataURL('image/jpeg', 0.6);
          }
          
          resolve(dataUrl);
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handlePhotoUpload = async (e, field) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error("Please upload an image file");
      return;
    }

    try {
      toast.info("Compressing photo...");
      const compressedImage = await compressImage(file, 200);
      setNewRepair({ ...newRepair, [field]: compressedImage });
      toast.success("Photo captured & compressed");
    } catch (error) {
      toast.error("Failed to process image");
      console.error("Image compression error:", error);
    }
  };

  const handleAddRepairType = async () => {
    if (!newRepairType.trim()) {
      toast.error("Please enter a repair type name");
      return;
    }
    try {
      const res = await axios.post(`${API}/admin/repair-types`, { name: newRepairType });
      setRepairTypes([...repairTypes, res.data]);
      setNewRepair({ ...newRepair, repair_type: res.data.name });
      setNewRepairType("");
      setShowAddType(false);
      toast.success("Repair type added!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add repair type");
    }
  };

  const handleEditRepairType = async () => {
    if (!editTypeName.trim()) {
      toast.error("Please enter a repair type name");
      return;
    }
    try {
      await axios.patch(`${API}/admin/repair-types/${editingType.id}`, { name: editTypeName });
      setRepairTypes(repairTypes.map(t => t.id === editingType.id ? { ...t, name: editTypeName } : t));
      if (newRepair.repair_type === editingType.name) {
        setNewRepair({ ...newRepair, repair_type: editTypeName });
      }
      setEditingType(null);
      setEditTypeName("");
      toast.success("Repair type updated!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update repair type");
    }
  };

  const handleDeleteRepairType = async (typeId, typeName) => {
    if (!window.confirm(`Are you sure you want to delete "${typeName}"?`)) return;
    try {
      await axios.delete(`${API}/admin/repair-types/${typeId}`);
      setRepairTypes(repairTypes.filter(t => t.id !== typeId));
      if (newRepair.repair_type === typeName) {
        setNewRepair({ ...newRepair, repair_type: "" });
      }
      toast.success("Repair type deleted!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete repair type");
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newRepair.repair_type) {
      toast.error("Please select a repair type");
      return;
    }
    if (!newRepair.mechanic_id) {
      toast.error("Please select a mechanic");
      return;
    }
    // Validate mandatory fields
    if (!newRepair.vehicle_number || !newRepair.vehicle_number.trim()) {
      toast.error("Vehicle number is required");
      return;
    }
    if (!newRepair.damage_photo) {
      toast.error("Damage photo is required");
      return;
    }
    if (!newRepair.vehicle_photo) {
      toast.error("Vehicle photo is required");
      return;
    }
    
    // Set loading state and close modal immediately for better UX
    setCreatingRepair(true);
    const repairData = {
      repair_type: newRepair.repair_type,
      mechanic_id: newRepair.mechanic_id,
      vehicle_number: newRepair.vehicle_number,
      damage_photo: newRepair.damage_photo,
      vehicle_photo: newRepair.vehicle_photo
    };
    
    // Close modal immediately (optimistic UI)
    setShowAddModal(false);
    toast.info("Saving repair...", { duration: 2000 });
    
    // Reset form state
    setNewRepair({
      repair_type: "",
      mechanic_id: "",
      vehicle_number: "",
      damage_photo: "",
      vehicle_photo: "",
      mechanicSearch: ""
    });
    setShowAddType(false);
    setNewRepairType("");
    
    try {
      await axios.post(`${API}/admin/repairs`, repairData);
      toast.success("Repair completed successfully!");
      fetchRepairs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to complete repair");
    } finally {
      setCreatingRepair(false);
    }
  };
  
  // Fetch and view photos for a repair
  const handleViewPhotos = async (repair) => {
    setViewingPhotos(repair);
    setPhotosLoading(true);
    setRepairPhotos({ damage_photo: null, vehicle_photo: null });
    
    try {
      const res = await axios.get(`${API}/repairs/${repair.id}/photos`);
      setRepairPhotos(res.data);
    } catch (error) {
      console.error("Error fetching photos:", error);
      toast.error("Failed to load photos");
    } finally {
      setPhotosLoading(false);
    }
  };
  
  // Download photo
  const handleDownloadPhoto = (photoData, filename) => {
    if (!photoData) return;
    const link = document.createElement('a');
    link.href = photoData;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleUpdate = async (repairId, updateData) => {
    try {
      await axios.patch(`${API}/admin/repairs/${repairId}`, updateData);
      toast.success("Repair updated successfully!");
      setEditingRepair(null);
      fetchRepairs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update repair");
    }
  };

  const handleStatusChange = async (repairId, newStatus) => {
    try {
      await axios.patch(`${API}/admin/repairs/${repairId}`, { status: newStatus });
      toast.success("Status updated!");
      fetchRepairs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update status");
    }
  };

  const handleClaim = async (repairId) => {
    try {
      await axios.post(`${API}/repairs/${repairId}/claim`);
      toast.success("Repair claimed — assigned to you");
      fetchRepairs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to claim repair");
    }
  };

  const handleDelete = async (repairId) => {
    if (!window.confirm("Are you sure you want to delete this repair? Items will be returned to inventory.")) return;
    try {
      await axios.delete(`${API}/admin/repairs/${repairId}`);
      toast.success("Repair deleted successfully!");
      fetchRepairs();
      fetchInventory();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete repair");
    }
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    if (!itemToAdd.item_id) {
      toast.error("Please select an item");
      return;
    }
    try {
      await axios.post(`${API}/admin/repairs/${addingItemToRepair.id}/add-item`, {
        item_id: itemToAdd.item_id,
        quantity: parseInt(itemToAdd.quantity)
      });
      toast.success("Item added to repair!");
      setAddingItemToRepair(null);
      setItemToAdd({ item_id: "", quantity: 1, itemSearch: "" });
      fetchRepairs();
      fetchInventory();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add item");
    }
  };

  const handleRemoveItem = async (repairId, itemId) => {
    if (!window.confirm("Remove this item? It will be returned to inventory.")) return;
    try {
      await axios.delete(`${API}/admin/repairs/${repairId}/remove-item/${itemId}`);
      toast.success("Item removed and returned to inventory!");
      fetchRepairs();
      fetchInventory();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove item");
    }
  };

  const selectMechanic = (mechanic) => {
    setNewRepair({
      ...newRepair,
      mechanic_id: mechanic.id,
      mechanicSearch: mechanic.name || mechanic.full_name
    });
  };

  const selectItem = (item) => {
    setItemToAdd({
      ...itemToAdd,
      item_id: item.id,
      itemSearch: `${item.name} (${item.quantity} available)`
    });
  };

  // Calculate stats
  const pendingCount = repairs.filter(r => r.status === 'pending').length;
  const inProgressCount = repairs.filter(r => r.status === 'in_progress').length;
  const completedCount = repairs.filter(r => r.status === 'completed').length;

  return (
    <div className="space-y-6 p-4">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
              <Wrench className="w-5 h-5 sm:w-6 sm:h-6 text-purple-500" />
              Repairs
            </h2>
            <p className="text-white/60 text-sm mt-1">
              {pendingCount} pending | {inProgressCount} in progress | {completedCount} completed
            </p>
          </div>
          <div className="flex flex-wrap gap-2 w-full sm:w-auto">
            <ImportExportButtons
              entityName="Repairs"
              exportEndpoint="/admin/repairs/export"
              importEndpoint="/admin/repairs/import"
              templateEndpoint="/admin/repairs/template"
              onImportSuccess={fetchRepairs}
              exportFileName="repairs"
            />
            <button
              onClick={() => setShowAddModal(true)}
              disabled={creatingRepair}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors whitespace-nowrap flex-1 sm:flex-none disabled:opacity-50"
              data-testid="add-repair-btn"
            >
              {creatingRepair ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  <span>Create Repair</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-[#161920] border border-white/10 p-4 space-y-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
            <input
              type="text"
              placeholder="Search repairs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500"
              data-testid="repair-search"
            />
          </div>
          <div className="flex gap-2">
            {['all', 'pending', 'in_progress', 'completed'].map(status => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`px-3 py-2 rounded-lg text-sm transition-colors ${
                  statusFilter === status
                    ? status === 'all' ? 'bg-purple-500 text-white' : getStatusColor(status)
                    : 'bg-white/5 text-white/60 hover:bg-white/10'
                }`}
                data-testid={`filter-${status}`}
              >
                {status === 'all' ? 'All' : status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </button>
            ))}
          </div>
        </div>
        
        {/* Date Range Filter */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-white/60" />
            <span className="text-sm text-white/60">Date Range:</span>
          </div>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="h-9 px-3 bg-white/5 border border-white/10 rounded-lg text-white text-sm [color-scheme:dark]"
            data-testid="repair-start-date"
          />
          <span className="text-white/40">to</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="h-9 px-3 bg-white/5 border border-white/10 rounded-lg text-white text-sm [color-scheme:dark]"
            data-testid="repair-end-date"
          />
          {(startDate || endDate || searchTerm || statusFilter !== 'all') && (
            <button
              onClick={clearFilters}
              className="h-9 px-3 bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/30 rounded-lg flex items-center gap-2"
            >
              <X className="w-4 h-4" />
              Clear
            </button>
          )}
          <span className="text-sm text-white/40 ml-auto">{filteredRepairs.length} repairs</span>
        </div>
      </div>

      {/* Repairs Table View */}
      <div className="bg-[#161920] border border-white/10 p-4">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10">
                <th 
                  className="text-left py-3 px-2 text-white/60 font-semibold cursor-pointer hover:text-white"
                  onClick={() => handleSort('created_at')}
                >
                  <div className="flex items-center gap-1">
                    Date
                    <ArrowUpDown className={`w-3 h-3 ${sortConfig.key === 'created_at' ? 'text-primary' : 'text-white/30'}`} />
                  </div>
                </th>
                <th 
                  className="text-left py-3 px-2 text-white/60 font-semibold cursor-pointer hover:text-white"
                  onClick={() => handleSort('repair_type')}
                >
                  <div className="flex items-center gap-1">
                    Repair Type
                    <ArrowUpDown className={`w-3 h-3 ${sortConfig.key === 'repair_type' ? 'text-primary' : 'text-white/30'}`} />
                  </div>
                </th>
                <th 
                  className="text-left py-3 px-2 text-white/60 font-semibold cursor-pointer hover:text-white"
                  onClick={() => handleSort('mechanic_name')}
                >
                  <div className="flex items-center gap-1">
                    Mechanic
                    <ArrowUpDown className={`w-3 h-3 ${sortConfig.key === 'mechanic_name' ? 'text-primary' : 'text-white/30'}`} />
                  </div>
                </th>
                <th 
                  className="text-left py-3 px-2 text-white/60 font-semibold cursor-pointer hover:text-white hidden sm:table-cell"
                  onClick={() => handleSort('vehicle_number')}
                >
                  <div className="flex items-center gap-1">
                    Vehicle
                    <ArrowUpDown className={`w-3 h-3 ${sortConfig.key === 'vehicle_number' ? 'text-primary' : 'text-white/30'}`} />
                  </div>
                </th>
                <th 
                  className="text-left py-3 px-2 text-white/60 font-semibold cursor-pointer hover:text-white"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center gap-1">
                    Status
                    <ArrowUpDown className={`w-3 h-3 ${sortConfig.key === 'status' ? 'text-primary' : 'text-white/30'}`} />
                  </div>
                </th>
                <th 
                  className="text-right py-3 px-2 text-white/60 font-semibold cursor-pointer hover:text-white"
                  onClick={() => handleSort('total_parts_cost')}
                >
                  <div className="flex items-center justify-end gap-1">
                    Parts Cost
                    <ArrowUpDown className={`w-3 h-3 ${sortConfig.key === 'total_parts_cost' ? 'text-primary' : 'text-white/30'}`} />
                  </div>
                </th>
                <th className="text-right py-3 px-2 text-white/60 font-semibold">Photos</th>
                <th className="text-right py-3 px-2 text-white/60 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRepairs.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-8 text-white/40">No repairs found</td>
                </tr>
              ) : (
                filteredRepairs.map((repair) => (
                  <tr key={repair.id} className="border-b border-white/5 hover:bg-white/5">
                    <td className="py-3 px-2 text-white/60 font-mono text-xs">{formatDateDMY(repair.created_at)}</td>
                    <td className="py-3 px-2">
                      <span className="text-white font-medium">{repair.title || repair.repair_type}</span>
                    </td>
                    <td className="py-3 px-2 text-white">
                      {repair.mechanic_name || (
                        <span className="text-yellow-400 italic text-xs">Unassigned</span>
                      )}
                      {repair.booking_source === 'driver' && repair.driver_name && (
                        <div className="text-xs text-blue-300 mt-0.5">
                          Driver: {repair.driver_name}
                          {repair.time_slot ? ` · ${repair.assigned_date} ${repair.time_slot}` : ''}
                        </div>
                      )}
                      {repair.issue_description && (
                        <div className="text-xs text-white/50 mt-0.5 truncate max-w-[220px]" title={repair.issue_description}>
                          {repair.issue_description}
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-2 text-white/60 font-mono hidden sm:table-cell">{repair.vehicle_number || '-'}</td>
                    <td className="py-3 px-2">
                      <div className="flex items-center gap-1">
                        <select
                          value={repair.status}
                          onChange={(e) => handleStatusChange(repair.id, e.target.value)}
                          className={`px-2 py-1 text-xs rounded border ${getStatusColor(repair.status)} bg-transparent`}
                        >
                          <option value="scheduled" className="bg-zinc-800">Scheduled</option>
                          <option value="pending" className="bg-zinc-800">Pending</option>
                          <option value="in_progress" className="bg-zinc-800">In Progress</option>
                          <option value="completed" className="bg-zinc-800">Completed</option>
                        </select>
                        {user?.role === 'mechanic' && !repair.mechanic_id && (
                          <button
                            onClick={() => handleClaim(repair.id)}
                            className="px-2 py-1 text-xs bg-green-500/20 text-green-400 border border-green-500/40 hover:bg-green-500/30 font-heading font-bold uppercase whitespace-nowrap"
                            title="Assign this repair to yourself"
                            data-testid={`claim-repair-${repair.id}`}
                          >
                            Claim
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-2 text-right text-green-400 font-mono">${repair.total_parts_cost?.toFixed(2) || '0.00'}</td>
                    <td className="py-3 px-2 text-center">
                      <button
                        onClick={() => handleViewPhotos(repair)}
                        className="p-1.5 text-purple-400 hover:bg-purple-500/20 rounded"
                        title="View Photos"
                        data-testid={`view-photos-${repair.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                    <td className="py-3 px-2 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => setAddingItemToRepair(repair)}
                          className="p-1.5 text-blue-400 hover:bg-blue-500/20 rounded"
                          title="Add parts"
                        >
                          <Package className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setEditingRepair(repair)}
                          className="p-1.5 text-white/60 hover:text-primary hover:bg-white/5 rounded"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(repair.id)}
                          className="p-1.5 text-white/60 hover:text-red-400 hover:bg-red-500/10 rounded"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Repairs Card View (keeping for reference) */}
      {false && <div className="space-y-4">
        {filteredRepairs.map((repair) => (
          <div key={repair.id} className="bg-white/5 rounded-xl p-4 border border-white/10">
            <div className="flex flex-col sm:flex-row justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-lg font-semibold text-white">{repair.title}</h3>
                  <span className={`px-2 py-1 text-xs rounded-full border flex items-center gap-1 ${getStatusColor(repair.status)}`}>
                    {getStatusIcon(repair.status)}
                    {repair.status.replace('_', ' ')}
                  </span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-white/40">Mechanic:</span>
                    <p className="text-white">{repair.mechanic_name}</p>
                  </div>
                  <div>
                    <span className="text-white/40">Vehicle:</span>
                    <p className="text-white">{repair.vehicle_number || "-"}</p>
                  </div>
                  <div>
                    <span className="text-white/40">Assigned:</span>
                    <p className="text-white">{formatDateDMY(repair.assigned_date)}</p>
                  </div>
                  <div>
                    <span className="text-white/40">Parts Cost:</span>
                    <p className="text-green-400 font-semibold">${repair.total_parts_cost?.toFixed(2)}</p>
                  </div>
                </div>
                {repair.description && (
                  <p className="text-white/60 text-sm mt-2">{repair.description}</p>
                )}
              </div>
              <div className="flex sm:flex-col gap-2 justify-end">
                <select
                  value={repair.status}
                  onChange={(e) => handleStatusChange(repair.id, e.target.value)}
                  className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  data-testid={`repair-status-${repair.id}`}
                >
                  <option value="pending">Pending</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                </select>
                <div className="flex gap-2">
                  <button
                    onClick={() => setAddingItemToRepair(repair)}
                    className="p-2 text-blue-400 hover:bg-blue-500/20 rounded-lg transition-colors"
                    title="Add parts"
                    data-testid={`add-parts-${repair.id}`}
                  >
                    <Package className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setEditingRepair(repair)}
                    className="p-2 text-orange-400 hover:bg-orange-500/20 rounded-lg transition-colors"
                    data-testid={`edit-repair-${repair.id}`}
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(repair.id)}
                    className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
                    data-testid={`delete-repair-${repair.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Items Used */}
            {repair.items_used && repair.items_used.length > 0 && (
              <div className="mt-4 pt-4 border-t border-white/10">
                <h4 className="text-sm font-medium text-white/60 mb-2">Parts Used:</h4>
                <div className="flex flex-wrap gap-2">
                  {repair.items_used.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2 px-3 py-1 bg-white/5 rounded-full text-sm">
                      <span className="text-white">{item.item_name}</span>
                      <span className="text-white/60">x{item.quantity}</span>
                      <button
                        onClick={() => handleRemoveItem(repair.id, item.item_id)}
                        className="text-red-400 hover:text-red-300"
                        data-testid={`remove-item-${item.item_id}`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
        {filteredRepairs.length === 0 && (
          <div className="bg-white/5 rounded-xl p-8 text-center text-white/40">
            No repairs found
          </div>
        )}
      </div>}

      {/* Add Repair Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-xl p-6 w-full max-w-lg border border-white/10 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-white mb-4">Create Repair Job</h3>
            <form onSubmit={handleCreate} className="space-y-4">
              
              {/* Repair Type Selection */}
              <div>
                <label className="block text-white/60 text-sm mb-2">Repair Type *</label>
                <div className="space-y-2 max-h-56 overflow-y-auto p-2 bg-white/5 border border-white/10 rounded-lg">
                  {repairTypes.map(type => (
                    <div key={type.id} className="flex items-center gap-2">
                      {editingType?.id === type.id ? (
                        <div className="flex-1 flex gap-2">
                          <input
                            type="text"
                            value={editTypeName}
                            onChange={(e) => setEditTypeName(e.target.value)}
                            className="flex-1 px-3 py-2 bg-zinc-700 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                            autoFocus
                          />
                          <button
                            type="button"
                            onClick={handleEditRepairType}
                            className="px-2 py-1 bg-green-500 text-white rounded text-sm hover:bg-green-600"
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            onClick={() => { setEditingType(null); setEditTypeName(""); }}
                            className="px-2 py-1 bg-zinc-600 text-white rounded text-sm hover:bg-zinc-500"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => setNewRepair({ ...newRepair, repair_type: type.name })}
                            className={`flex-1 px-3 py-2 rounded-lg text-sm transition-colors text-left ${
                              newRepair.repair_type === type.name
                                ? "bg-purple-500 text-white"
                                : "bg-zinc-700 text-white/80 hover:bg-zinc-600"
                            }`}
                            data-testid={`repair-type-${type.id}`}
                          >
                            {type.name}
                          </button>
                          <button
                            type="button"
                            onClick={() => { setEditingType(type); setEditTypeName(type.name); }}
                            className="p-1.5 text-blue-400 hover:bg-blue-500/20 rounded transition-colors"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteRepairType(type.id, type.name)}
                            className="p-1.5 text-red-400 hover:bg-red-500/20 rounded transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  ))}
                </div>
                
                {/* Add new repair type */}
                {showAddType ? (
                  <div className="mt-2 flex gap-2">
                    <input
                      type="text"
                      value={newRepairType}
                      onChange={(e) => setNewRepairType(e.target.value)}
                      placeholder="New repair type..."
                      className="flex-1 px-3 py-2 bg-zinc-800 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                      data-testid="new-repair-type-input"
                    />
                    <button
                      type="button"
                      onClick={handleAddRepairType}
                      className="px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 text-sm"
                    >
                      Add
                    </button>
                    <button
                      type="button"
                      onClick={() => { setShowAddType(false); setNewRepairType(""); }}
                      className="px-3 py-2 bg-zinc-700 text-white rounded-lg hover:bg-zinc-600 text-sm"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowAddType(true)}
                    className="mt-2 text-sm text-purple-400 hover:text-purple-300 flex items-center gap-1"
                  >
                    <Plus className="w-4 h-4" /> Add new repair type
                  </button>
                )}
              </div>

              {/* Mechanic Autocomplete */}
              <div className="relative">
                <label className="block text-white/60 text-sm mb-1">Assign Mechanic *</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newRepair.mechanicSearch}
                    onChange={(e) => setNewRepair({ ...newRepair, mechanicSearch: e.target.value, mechanic_id: "" })}
                    placeholder="Search mechanic..."
                    className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    required={!newRepair.mechanic_id}
                    data-testid="new-repair-mechanic-search"
                  />
                  {newRepair.mechanic_id && (
                    <button
                      type="button"
                      onClick={() => setNewRepair({ ...newRepair, mechanic_id: "", mechanicSearch: "" })}
                      className="p-2 text-white/60 hover:text-white"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </div>
                {newRepair.mechanicSearch && !newRepair.mechanic_id && filteredMechanics.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full bg-zinc-800 border border-white/10 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {filteredMechanics.map(m => (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => selectMechanic(m)}
                        className="w-full text-left px-3 py-2 text-white hover:bg-white/10"
                      >
                        {m.name || m.full_name}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Vehicle Number */}
              <div>
                <label className="block text-white/60 text-sm mb-1">Vehicle Number *</label>
                <input
                  type="text"
                  value={newRepair.vehicle_number}
                  onChange={(e) => setNewRepair({ ...newRepair, vehicle_number: e.target.value })}
                  placeholder="e.g., SGX-1234A"
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  required
                  data-testid="new-repair-vehicle"
                />
              </div>

              {/* Damage Photo */}
              <div>
                <label className="block text-white/60 text-sm mb-1">Photo of Damage *</label>
                <input
                  type="file"
                  ref={damagePhotoRef}
                  onChange={(e) => handlePhotoUpload(e, 'damage_photo')}
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                />
                {newRepair.damage_photo ? (
                  <div className="relative">
                    <img
                      src={newRepair.damage_photo}
                      alt="Damage"
                      className="w-full h-32 object-cover rounded-lg border border-white/10"
                    />
                    <button
                      type="button"
                      onClick={() => setNewRepair({ ...newRepair, damage_photo: "" })}
                      className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => damagePhotoRef.current?.click()}
                    className="w-full p-4 border-2 border-dashed border-white/20 rounded-lg text-white/60 hover:border-purple-500 hover:text-purple-400 transition-colors flex items-center justify-center gap-2"
                    data-testid="upload-damage-photo"
                  >
                    <Camera className="w-5 h-5" />
                    Upload Damage Photo (Required)
                  </button>
                )}
              </div>

              {/* Vehicle Photo */}
              <div>
                <label className="block text-white/60 text-sm mb-1">Vehicle Photo *</label>
                <input
                  type="file"
                  ref={vehiclePhotoRef}
                  onChange={(e) => handlePhotoUpload(e, 'vehicle_photo')}
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                />
                {newRepair.vehicle_photo ? (
                  <div className="relative">
                    <img
                      src={newRepair.vehicle_photo}
                      alt="Vehicle"
                      className="w-full h-32 object-cover rounded-lg border border-white/10"
                    />
                    <button
                      type="button"
                      onClick={() => setNewRepair({ ...newRepair, vehicle_photo: "" })}
                      className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => vehiclePhotoRef.current?.click()}
                    className="w-full p-4 border-2 border-dashed border-white/20 rounded-lg text-white/60 hover:border-purple-500 hover:text-purple-400 transition-colors flex items-center justify-center gap-2"
                    data-testid="upload-vehicle-photo"
                  >
                    <ImageIcon className="w-5 h-5" />
                    Upload Vehicle Photo (Required)
                  </button>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setNewRepair({
                      repair_type: "",
                      mechanic_id: "",
                      vehicle_number: "",
                      damage_photo: "",
                      vehicle_photo: "",
                      mechanicSearch: ""
                    });
                    setShowAddType(false);
                    setNewRepairType("");
                  }}
                  className="flex-1 px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creatingRepair}
                  className="flex-1 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="submit-new-repair"
                >
                  {creatingRepair ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Saving...
                    </>
                  ) : (
                    'Complete'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Repair Modal */}
      {editingRepair && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-xl p-6 w-full max-w-lg border border-white/10 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-white mb-4">Edit Repair</h3>
            <form onSubmit={(e) => { e.preventDefault(); handleUpdate(editingRepair.id, {
              title: editingRepair.title,
              description: editingRepair.description,
              vehicle_number: editingRepair.vehicle_number,
              assigned_date: editingRepair.assigned_date,
              notes: editingRepair.notes
            }); }} className="space-y-4">
              <div>
                <label className="block text-white/60 text-sm mb-1">Title</label>
                <input
                  type="text"
                  value={editingRepair.title}
                  onChange={(e) => setEditingRepair({ ...editingRepair, title: e.target.value })}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  data-testid="edit-repair-title"
                />
              </div>

              <div>
                <label className="block text-white/60 text-sm mb-1">Description</label>
                <textarea
                  value={editingRepair.description || ""}
                  onChange={(e) => setEditingRepair({ ...editingRepair, description: e.target.value })}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  rows={2}
                  data-testid="edit-repair-description"
                />
              </div>

              <div>
                <label className="block text-white/60 text-sm mb-1">Mechanic</label>
                <input
                  type="text"
                  value={editingRepair.mechanic_name}
                  disabled
                  className="w-full px-3 py-2 bg-white/10 border border-white/10 rounded-lg text-white/60"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-white/60 text-sm mb-1">Vehicle Number</label>
                  <input
                    type="text"
                    value={editingRepair.vehicle_number || ""}
                    onChange={(e) => setEditingRepair({ ...editingRepair, vehicle_number: e.target.value })}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    data-testid="edit-repair-vehicle"
                  />
                </div>
                <div>
                  <label className="block text-white/60 text-sm mb-1">Assigned Date</label>
                  <input
                    type="date"
                    value={editingRepair.assigned_date}
                    onChange={(e) => setEditingRepair({ ...editingRepair, assigned_date: e.target.value })}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    data-testid="edit-repair-date"
                  />
                </div>
              </div>

              <div>
                <label className="block text-white/60 text-sm mb-1">Notes</label>
                <textarea
                  value={editingRepair.notes || ""}
                  onChange={(e) => setEditingRepair({ ...editingRepair, notes: e.target.value })}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  rows={2}
                  data-testid="edit-repair-notes"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setEditingRepair(null)}
                  className="flex-1 px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
                  data-testid="submit-edit-repair"
                >
                  Update
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Item to Repair Modal */}
      {addingItemToRepair && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-xl p-6 w-full max-w-md border border-white/10">
            <h3 className="text-xl font-bold text-white mb-2">Add Parts to Repair</h3>
            <p className="text-white/60 text-sm mb-4">{addingItemToRepair.title}</p>
            <form onSubmit={handleAddItem} className="space-y-4">
              {/* Item Autocomplete */}
              <div className="relative">
                <label className="block text-white/60 text-sm mb-1">Select Item *</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={itemToAdd.itemSearch}
                    onChange={(e) => setItemToAdd({ ...itemToAdd, itemSearch: e.target.value, item_id: "" })}
                    placeholder="Search item..."
                    className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="add-item-search"
                  />
                  {itemToAdd.item_id && (
                    <button
                      type="button"
                      onClick={() => setItemToAdd({ ...itemToAdd, item_id: "", itemSearch: "" })}
                      className="p-2 text-white/60 hover:text-white"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </div>
                {itemToAdd.itemSearch && !itemToAdd.item_id && filteredItems.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full bg-zinc-800 border border-white/10 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {filteredItems.map(i => (
                      <button
                        key={i.id}
                        type="button"
                        onClick={() => selectItem(i)}
                        className="w-full text-left px-3 py-2 text-white hover:bg-white/10"
                      >
                        {i.name} ({i.quantity} available)
                      </button>
                    ))}
                  </div>
                )}
                {itemToAdd.itemSearch && !itemToAdd.item_id && filteredItems.length === 0 && (
                  <div className="absolute z-10 mt-1 w-full bg-zinc-800 border border-white/10 rounded-lg shadow-lg p-3 text-white/40 text-center">
                    No items found or all out of stock
                  </div>
                )}
              </div>

              <div>
                <label className="block text-white/60 text-sm mb-1">Quantity *</label>
                <input
                  type="number"
                  value={itemToAdd.quantity}
                  onChange={(e) => setItemToAdd({ ...itemToAdd, quantity: e.target.value })}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="1"
                  required
                  data-testid="add-item-quantity"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setAddingItemToRepair(null); setItemToAdd({ item_id: "", quantity: 1, itemSearch: "" }); }}
                  className="flex-1 px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                  data-testid="submit-add-item"
                >
                  Add Item
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Photos Modal */}
      {viewingPhotos && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-xl p-6 w-full max-w-2xl border border-white/10 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-white">
                Repair Photos - {viewingPhotos.title || viewingPhotos.repair_type}
              </h3>
              <button
                onClick={() => {
                  setViewingPhotos(null);
                  setRepairPhotos({ damage_photo: null, vehicle_photo: null });
                }}
                className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {photosLoading ? (
              <div className="flex justify-center items-center py-12">
                <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
                <span className="ml-3 text-white/60">Loading photos...</span>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Damage Photo */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="text-white/80 font-medium">Damage Photo</h4>
                    {repairPhotos.damage_photo && (
                      <button
                        onClick={() => handleDownloadPhoto(repairPhotos.damage_photo, `damage-${viewingPhotos.vehicle_number || 'repair'}.jpg`)}
                        className="flex items-center gap-1 text-sm text-purple-400 hover:text-purple-300"
                      >
                        <Download className="w-4 h-4" />
                        Download
                      </button>
                    )}
                  </div>
                  {repairPhotos.damage_photo ? (
                    <img
                      src={repairPhotos.damage_photo}
                      alt="Damage"
                      className="w-full max-h-72 object-contain rounded-lg border border-white/10 bg-black"
                    />
                  ) : (
                    <div className="w-full h-32 bg-white/5 rounded-lg border border-white/10 flex items-center justify-center text-white/40">
                      No damage photo uploaded
                    </div>
                  )}
                </div>

                {/* Vehicle Photo */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="text-white/80 font-medium">Vehicle Photo</h4>
                    {repairPhotos.vehicle_photo && (
                      <button
                        onClick={() => handleDownloadPhoto(repairPhotos.vehicle_photo, `vehicle-${viewingPhotos.vehicle_number || 'repair'}.jpg`)}
                        className="flex items-center gap-1 text-sm text-purple-400 hover:text-purple-300"
                      >
                        <Download className="w-4 h-4" />
                        Download
                      </button>
                    )}
                  </div>
                  {repairPhotos.vehicle_photo ? (
                    <img
                      src={repairPhotos.vehicle_photo}
                      alt="Vehicle"
                      className="w-full max-h-72 object-contain rounded-lg border border-white/10 bg-black"
                    />
                  ) : (
                    <div className="w-full h-32 bg-white/5 rounded-lg border border-white/10 flex items-center justify-center text-white/40">
                      No vehicle photo uploaded
                    </div>
                  )}
                </div>

                {/* Repair Details */}
                <div className="pt-4 border-t border-white/10">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-white/40">Vehicle:</span>
                      <p className="text-white">{viewingPhotos.vehicle_number || '-'}</p>
                    </div>
                    <div>
                      <span className="text-white/40">Mechanic:</span>
                      <p className="text-white">{viewingPhotos.mechanic_name}</p>
                    </div>
                    <div>
                      <span className="text-white/40">Date:</span>
                      <p className="text-white">{formatDateDMY(viewingPhotos.created_at)}</p>
                    </div>
                    <div>
                      <span className="text-white/40">Status:</span>
                      <span className={`px-2 py-0.5 text-xs rounded ${getStatusColor(viewingPhotos.status)}`}>
                        {viewingPhotos.status?.replace('_', ' ')}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end mt-6">
              <button
                onClick={() => {
                  setViewingPhotos(null);
                  setRepairPhotos({ damage_photo: null, vehicle_photo: null });
                }}
                className="px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
